// mantine
import { Button, Textarea, TextInput, UnstyledButton } from '@mantine/core';
// utils
import { useFormik } from 'formik';
import { processFormData } from '@newo/toolkit/bff';
import { TPersona, TPersonaData } from '@newo/api/gateway';
import validationSchema from '@/modules/Personas/components/PersonaForm/PersonaForm.schema';
import {
    useCreatePersonaMutation,
    useUpdatePersonaMutation
} from '@/services/customer/personas/personas.queries.ts';

interface PersonaFormProps {
    persona?: TPersona;
    onClose?: () => void;
    onSubmit?: (personaId: string, data: TPersonaData) => void;
}

const PersonaForm = ({ persona, onClose, onSubmit }: PersonaFormProps) => {
    const mutationAdd = useCreatePersonaMutation();
    const mutationUpdate = useUpdatePersonaMutation();

    const initialValues: TPersonaData = {
        name: persona?.name ?? '',
        title: persona?.title ?? '',
        description: persona?.description ?? '',
        agent: persona?.agent ?? undefined
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const data = processFormData(formData);
            if (persona) {
                mutationUpdate.mutate(
                    { personaId: persona.id, data },
                    {
                        onSuccess() {
                            onSubmit?.(persona.id, data);
                            onClose?.();
                        },
                        onSettled() {
                            formik.setSubmitting(false);
                        }
                    }
                );
                return;
            }
            mutationAdd.mutate(
                { data },
                {
                    onSuccess(response) {
                        onSubmit?.(response.id, data);
                        onClose?.();
                    },
                    onSettled() {
                        formik.setSubmitting(false);
                    }
                }
            );
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Personas name"
                error={
                    formik.touched.name && formik.values.name
                        ? formik.errors.name
                        : undefined
                }
                label="Name"
                name="name"
                placeholder="Personas name"
                value={formik.values.name}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <TextInput
                description="Personas title"
                error={
                    formik.touched.title && formik.values.title
                        ? formik.errors.title
                        : undefined
                }
                label="Title"
                name="title"
                placeholder="Personas title"
                value={formik.values.title}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <Textarea
                description="Personas description"
                error={
                    formik.touched.description && formik.values.description
                        ? formik.errors.description
                        : undefined
                }
                label="Description"
                name="description"
                placeholder="Personas description"
                rows={4}
                value={formik.values.description}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {persona ? 'Update' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default PersonaForm;
